// Line Items — the Line items custom field on a work item: an always-open
// table drawn from the field's definition (Settings → Fields), in the look
// of a purchase document — plain headings over a rule, the product in bold,
// quantities as chips, money right-aligned, the totals block under it.
//   · click a cell to change it — it saves on its own; formula cells are
//     worked out by the server (the same numbers a report prints)
//   · the last row adds a line: type a product (the catalogue's ZQL is
//     searched by name, key and the shown fields) and pick it — the line
//     SAVES ITSELF the moment every required column has a value (the
//     definition's defaults count: quantity 1, a batch number that is the
//     work item's key), and its cells are then changed like any other's.
//     A name not in the catalogue can be kept as free text or, when the
//     field allows it, created as a new product on the spot.
//   · every add / change / removal is a History entry under the field.
//   · a table with a DOCUMENT (definition.document) is drawn AS that
//     document: the letterhead, the number (and its barcode, or a QR code
//     of the work item's link) and date, the two parties, the lines, the
//     totals — with its PDF one click away and a full-screen view. A
//     document that converts (a quote → an order) carries the toggle; once
//     converted with `lock`, the lines are final until the work item moves
//     back (the server refuses the writes; the table drops its editing).
//     With convert.sign the document carries an AUTHORISATION section —
//     its steps signed in order (SignPad.jsx: requester, then director);
//     the converting step's signature makes it the order, and every
//     signature prints on the order and on its quote.
//   · a product column with a `card` lists the catalogue as cards (the
//     category chip, the quantity on hand, or NON-STOCKED / SERVICE), opens
//     on focus, and creates a missing product only with the fields the
//     definition asks for (createProduct.ask — e.g. Stock Type).
//   · a column with `fillFromLast` fills a new line from the product's most
//     recent line on a work item in Done (the unit cost last paid).
//   · a column with `fillFrom` takes a field of the picked product (through
//     `via`: a batch's Stock Item, for its Standard Price); `readOnly` = never
//     typed, the server sets it. A `max` column (a batch's Qty On Hand) shows
//     "of N on hand" and refuses more; a card with `value` shows what the
//     batch is worth.
//   · `lockStatuses`: in those statuses the lines are final (a pull
//     allocated); `editGroups`: only those groups change them.
//   · DraftLines: the same table for a work item that does not exist yet (a
//     create form) — kept in the browser, computed by the server.
// LineItemsTable is also the definition editor's live preview.
// A field whose definition says view.kind = 'checklist' is drawn as a
// checklist instead (ChecklistView.jsx) — the same lines, read as checks;
// view.kind = 'plan' as a plan (PlanView.jsx) — the lines read as its moves.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import api, { extractErrorMessage } from '@app/services/api';
import { useLookAndFeel } from '@platform/app-shell/frontend/lookAndFeel';
import lineItemsApi from '@modules/database/line-items/frontend/api';
import { socket } from '@platform/http/socket';
import { createPortal } from 'react-dom';
import { code128, qrLink } from '@modules/database/line-items/frontend/document-pdf';
import { Authorisation } from '@modules/database/line-items/frontend/SignPad';
import ChecklistView from '@modules/database/line-items/frontend/ChecklistView';
import PlanView from '@modules/database/line-items/frontend/PlanView';
import { useOptionalTab } from '@platform/app-shell/frontend/contexts/TabContext';
import offlineReportsApi, { errorMessage as reportError, triggerBlobDownload } from '@modules/reports/frontend/api';
import SearchableSelect from '@platform/ui/SearchableSelect';
import UserEditor from '@modules/database/work-items/frontend/editors/UserEditor';
import { useUserSearch } from '@platform/query';
import '@modules/database/line-items/frontend/LineItems.css';

const errText = (e) => (typeof extractErrorMessage === 'function' ? extractErrorMessage(e) : null) || e?.response?.data?.error || e?.message || 'Something went wrong';
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const zqlStr = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
const display = (v) => {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return v.map(display).filter(Boolean).join(', ');
  if (typeof v === 'object') return String(v.value ?? v.name ?? v.displayName ?? v.key ?? '');
  return String(v);
};

// Display only — the server's model.js does the arithmetic.
const NUMERIC = new Set(['number', 'money', 'formula']);
const isMoney = (c) => c.type === 'money' || (c.type === 'formula' && c.format !== 'number');
function fmtNumber(n, decimals) {
  if (n == null || !Number.isFinite(Number(n))) return '';
  return Number(n).toLocaleString('en-ZA', decimals == null ? { maximumFractionDigits: 3 } : { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
export const fmtMoney = (n, currency = 'R', decimals = 2) => (n == null || !Number.isFinite(Number(n)) ? '' : `${currency} ${fmtNumber(n, decimals)}`);
const fmtDmy = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(String(iso || '')) ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : '—');
function cellText(def, col, line) {
  // worked out from the work item's Due date: read-only, dd/mm/yyyy
  if (col.fromDue) return fmtDmy(line.calc ? line.calc[col.id] : null);
  if (NUMERIC.has(col.type)) {
    const v = line.calc ? line.calc[col.id] : line.vals?.[col.id];
    return isMoney(col) ? fmtMoney(v, def.currency, col.decimals ?? 2) : fmtNumber(v, col.decimals);
  }
  return display(line.vals?.[col.id]);
}
const alignClass = (c) => `li-a-${c.align || (NUMERIC.has(c.type) ? 'right' : 'left')}`;
// "Qty On Hand" → "on hand" (the words under a capped quantity).
const capWord = (col) => String(col.max?.field || '').replace(/^qty\s+/i, '').toLowerCase() || 'available';

// The site's fields, once per page load (name → { id, schema }).
let fieldsPromise = null;
function useFieldsByName() {
  const [map, setMap] = useState(null);
  useEffect(() => {
    if (!fieldsPromise) fieldsPromise = api.getFields().catch(() => []);
    let on = true;
    fieldsPromise.then((list) => { if (on) setMap(new Map((Array.isArray(list) ? list : []).map((f) => [norm(f.name), f]))); });
    return () => { on = false; };
  }, []);
  return map;
}

// The options of every option column (each from its field, for this work item).
function useColumnOptions(def, fields, issueKey, projectKey) {
  const [options, setOptions] = useState({});   // colId → [{ id, value }]
  useEffect(() => {
    if (!fields || !def) return;
    def.columns.filter((c) => c.type === 'option').forEach((c) => {
      const f = fields.get(norm(c.field));
      if (!f) { setOptions((o) => ({ ...o, [c.id]: [] })); return; }
      api.getFieldOptions(f.id, projectKey, undefined, issueKey)
        .then((d) => setOptions((o) => ({ ...o, [c.id]: (Array.isArray(d) ? d : d?.values || d?.options || []).filter((x) => !x.disabled) })))
        .catch(() => setOptions((o) => ({ ...o, [c.id]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, issueKey, projectKey, def]);
  return options;
}

// onWorkItemStatus(status): a checklist whose status follows its lines moved the
// work item — the screen shows the new status at once.
export default function LineItemsField({ issueKey, fieldId, onWorkItemStatus }) {
  const [state, setState] = useState(null);   // { canEdit, projectKey, definition, lines, totals }
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    if (!issueKey || !fieldId) return;
    lineItemsApi.forField(issueKey, fieldId).then((d) => { setState(d); setError(null); }).catch((e) => setError(errText(e)));
  }, [issueKey, fieldId]);
  useEffect(() => { setState(null); load(); }, [load]);
  if (error && !state) return <div className="li-error">{error}</div>;
  if (!state) return <div className="li-muted li-pad">Loading…</div>;
  if (!state.definition) return <div className="li-muted li-pad">This field has no columns yet — an admin defines them in Settings → Fields.</div>;
  // onLocal: change the lines on screen at once (a new line shows before
  // the server has answered; the reload that follows puts the real one in).
  const onLocal = (fn) => setState((s) => (s ? { ...s, lines: fn(s.lines) } : s));
  if (state.definition.view?.kind === 'checklist') return <ChecklistField issueKey={issueKey} fieldId={fieldId} state={state} onChanged={load} onWorkItemStatus={onWorkItemStatus} />;
  // a plan: drawn as its moves; the table is one click away inside it
  if (state.definition.view?.kind === 'plan') {
    return (
      <PlanView issueKey={issueKey} fieldId={fieldId} state={state} onChanged={load}>
        <EditableTable issueKey={issueKey} fieldId={fieldId} state={state} onChanged={load} onLocal={onLocal} />
      </PlanView>
    );
  }
  return <EditableTable issueKey={issueKey} fieldId={fieldId} state={state} onChanged={load} onLocal={onLocal} onWorkItemStatus={onWorkItemStatus} />;
}

function ChecklistField({ issueKey, fieldId, state, onChanged, onWorkItemStatus }) {
  const fields = useFieldsByName();
  const options = useColumnOptions(state.definition, fields, issueKey, state.projectKey);
  return <ChecklistView issueKey={issueKey} fieldId={fieldId} state={state} onChanged={onChanged} onWorkItemStatus={onWorkItemStatus} options={options} />;
}

function EditableTable({ issueKey, fieldId, state, onChanged, onLocal, onWorkItemStatus }) {
  const fields = useFieldsByName();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const def = state.definition;
  const options = useColumnOptions(def, fields, issueKey, state.projectKey);

  const run = async (fn) => {
    setBusy(true); setError(null);
    // A refused write reloads too, so a line drawn ahead of the server goes.
    try { await fn(); onChanged(); } catch (e) { setError(errText(e)); onChanged(); }
    setBusy(false);
  };
  const lines = state.lines;
  // A converted document with lock, or a status the definition locks the
  // lines in: the lines are final (the server says so too).
  const locked = !!state.document?.locked || !!state.locked;
  // A product created from a line that has let it go: archived — or kept, and why.
  const [archNote, setArchNote] = useState(null);
  const noteArchived = (r) => {
    const a = r && r.archived;
    if (!a || !a.key) return;
    setArchNote(a.archived ? `${a.key} was created for that line and is no longer used — it has been archived.` : `${a.key} was created for that line but is kept: ${a.reason}.`);
    setTimeout(() => setArchNote(null), 8000);
  };
  // A final document's BOOKING columns (book: 'enter' — an order's Received): on the screen once it is
  // final, and still typed in by whoever may edit the lines; nothing else of the document changes.
  const bookCols = (def.columns || []).filter((c) => c.book);
  const showBook = !!state.document?.locked && !state.lockedStatus && bookCols.length > 0;
  const bookOnly = showBook && state.canEdit && bookCols.some((c) => c.book === 'enter');
  const handlers = state.canEdit && !locked ? {
    onSave: (line, patch) => run(async () => noteArchived(await lineItemsApi.updateLine(line.id, { productKey: line.productKey, productLabel: line.productLabel, ...patch, vals: { ...line.vals, ...(patch.vals || {}) } }))),
    // On a touch screen a ✕ is easily hit by a scrolling finger: ask first.
    onRemove: (line) => {
      const touch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches;
      if (touch && !window.confirm(`Remove the line “${line.productLabel || line.productKey || 'this line'}”?`)) return undefined;
      return run(async () => noteArchived(await lineItemsApi.deleteLine(line.id)));
    },
    onMove: (i, dir) => {
      const ids = lines.map((l) => l.id); const j = i + dir;
      if (j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      run(() => lineItemsApi.reorder(issueKey, fieldId, ids));
    },
    onAdd: (line) => {
      // Drawn at once, greyed, until the server's line replaces it.
      onLocal((ls) => [...ls, { ...line, id: `pending-${Date.now()}`, pending: true, calc: {} }]);
      return run(() => lineItemsApi.addLine(issueKey, fieldId, line));
    },
    // An editable percent total's rate on this work item (VAT 0 %).
    onRate: (total, rate) => run(() => lineItemsApi.setRate(issueKey, fieldId, total.id, rate)),
  } : bookOnly ? {
    bookOnly: true,
    onSave: (line, patch) => run(async () => noteArchived(await lineItemsApi.updateLine(line.id, { productKey: line.productKey, productLabel: line.productLabel, ...patch, vals: { ...line.vals, ...(patch.vals || {}) } }))),
  } : null;
  // A document's paper shows what it prints: a print: false column (what the warehouse books against a
  // line) stays in the line's values — a change merges into them — but off the page.
  const onPaper = def.document ? { ...def, columns: (def.columns || []).filter((c) => c.print !== false || (showBook && c.book)) } : def;
  const table = (
    <LineItemsTable definition={onPaper} lines={lines} totals={state.totals} handlers={handlers} busy={busy} issueKey={issueKey}
      options={options} fields={fields} projectKey={state.projectKey} fieldId={fieldId} />
  );
  const groups = def.editGroups || [];
  return (
    <>
      {error && <div className="li-error">{error}</div>}
      {archNote && <div className="li-muted li-note" role="status">{archNote}</div>}
      {state.lockedStatus && <LockNote text={`Final in ${state.lockedStatus} — no line can be added, changed or removed.`} />}
      {!state.canEdit && !locked && groups.length > 0 && <div className="li-muted li-note">Only {groups.join(' / ')} can change these lines.</div>}
      {state.readBack && <div className="li-muted li-note">The work items whose “{state.readBack}” names this one — change it on those work items.</div>}
      {def.document
        ? <DocumentView state={state} issueKey={issueKey} fieldId={fieldId} fields={fields} onChanged={onChanged} onWorkItemStatus={onWorkItemStatus}
          downloads={<ReportDownloads downloads={def.downloads} issueKey={issueKey} status={state.status} inPaper />}>{table}</DocumentView>
        : <>{table}<ReportDownloads downloads={def.downloads} issueKey={issueKey} status={state.status} /></>}
    </>
  );
}

// The reports the definition offers under the table (definition.downloads):
// one button per format the report allows (Excel, PDF), each report only in
// the statuses it names — the invoice sheet, or the credit note once the credit
// process has started. Rendered on the server from the work item as it is now.
function ReportDownloads({ downloads, issueKey, status, inPaper = false }) {
  const st = String(status || '').toLowerCase();
  const shown = (downloads || []).filter((d) => !(d.statuses || []).length || d.statuses.some((x) => String(x).toLowerCase() === st));
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!shown.length) return undefined;
    let live = true;
    offlineReportsApi.listReports({ surface: 'item' }).then((rows) => { if (live) setReports(Array.isArray(rows) ? rows : []); }).catch(() => { if (live) setReports([]); });
    return () => { live = false; };
  }, [shown.length]);
  if (!shown.length || !reports) return null;
  const go = async (row, fmt) => {
    setBusy(`${row.id}:${fmt}`); setErr(null);
    try {
      const { blob, filename } = await offlineReportsApi.renderReport(row.id, { issueKey, outputFormat: fmt });
      triggerBlobDownload(blob, filename);
    } catch (e) { setErr(await reportError(e, 'Could not build the file')); }
    setBusy(null);
  };
  const items = shown.map((d) => ({ d, row: reports.find((r) => r.slug === d.report) })).filter((x) => x.row);
  if (!items.length) return null;
  return (
    <div className={`li-downloads${inPaper ? ' li-downloads-in-paper' : ''}`}>
      {items.map(({ d, row }) => (
        <div key={d.report} className="li-download">
          <span className="li-download-name">{d.label || row.name}</span>
          {(row.output_formats || []).filter((f) => f === 'xlsx' || f === 'pdf').map((f) => (
            <button key={f} type="button" className="li-btn" disabled={!!busy} onClick={() => go(row, f)} title={`Download ${d.label || row.name} as ${f === 'xlsx' ? 'Excel' : 'PDF'}`}>
              {busy === `${row.id}:${f}` ? 'Building…' : (f === 'xlsx' ? 'Excel' : 'PDF')}
            </button>
          ))}
        </div>
      ))}
      {err && <div className="li-error">{err}</div>}
    </div>
  );
}

function LockNote({ text }) {
  return (
    <div className="li-lock" role="status">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
      <span>{text}</span>
    </div>
  );
}

// ---- lines before the work item exists (a create form) --------------------------
// The field's table, kept in the browser: every change is sent to the
// server's draft check, which fills read-only columns from the products,
// checks the caps and works the formulas and totals out — the numbers the
// saved lines will have. onChange({ lines, totals, errors }) after each.
// paper = { number, date, party, valueOf? }: a table with a document is
// drawn ON its template (letterhead, title, number, date, party) — the page
// the PDF prints, filled in before the work item exists. valueOf(fieldName)
// reads the form around it (a create screen): the party and the date follow
// what is chosen there, as they follow the work item's fields on its screen.
let draftSeq = 0;
// initial: lines to open with — [{ productKey, productLabel, qty?, vals? }]
// (a replenishment order from a filtered stock list): each is filled like a
// product picked by hand (its last price, what the product brings), and
// `qty` goes into the quantity column.
export function DraftLines({ fieldId, onChange, projectKey = null, paper = null, initial = null }) {
  const lookAndFeel = useLookAndFeel();
  const fields = useFieldsByName();
  const [def, setDef] = useState(null);
  const [lines, setLines] = useState([]);
  const [totals, setTotals] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const compute = useCallback(async (next) => {
    const n = ++seq.current;
    setBusy(true);
    try {
      const r = await lineItemsApi.draft(fieldId, next.map((l) => ({ productKey: l.productKey, productLabel: l.productLabel, vals: l.vals })));
      if (n !== seq.current) return;
      const shaped = (r.lines || []).map((l, i) => ({ ...l, id: next[i]?.id ?? `d${i}` }));
      setDef(r.definition); setLines(shaped); setTotals(r.totals || []); setError(null);
      if (onChange) onChange({ lines: shaped, totals: r.totals || [], errors: shaped.flatMap((l) => l.errors || []) });
    } catch (e) { if (n === seq.current) setError(errText(e)); }
    if (n === seq.current) setBusy(false);
  }, [fieldId, onChange]);
  useEffect(() => {
    if (!initial || !initial.length) { compute([]); return undefined; }
    let on = true;
    (async () => {
      setBusy(true);
      try {
        const d = (await lineItemsApi.draft(fieldId, [])).definition;
        const qtyCol = (d.columns || []).find((c) => c.type === 'number' && /qty|quantit/i.test(`${c.id} ${c.label}`)) || (d.columns || []).find((c) => c.type === 'number');
        const filled = await Promise.all(initial.map(async (l) => {
          const fill = await fillFromLast(d, fieldId, { key: l.productKey });
          return { id: `d${++draftSeq}`, productKey: l.productKey, productLabel: l.productLabel, vals: { ...fill, ...(l.vals || {}), ...(qtyCol && l.qty != null ? { [qtyCol.id]: l.qty } : {}) } };
        }));
        if (on) await compute(filled);
      } catch (e) { if (on) { setError(errText(e)); setBusy(false); } }
    })();
    return () => { on = false; };
  }, [fieldId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error && !def) return <div className="li-error">{error}</div>;
  if (!def) return <div className="li-muted li-pad">Loading…</div>;
  const handlers = {
    onAdd: (line) => compute([...lines, { ...line, id: `d${++draftSeq}` }]),
    onSave: (line, patch) => compute(lines.map((l) => (l.id === line.id ? { ...l, ...patch, vals: { ...l.vals, ...(patch.vals || {}) } } : l))),
    onRemove: (line) => compute(lines.filter((l) => l.id !== line.id)),
    onMove: (i, dir) => { const j = i + dir; if (j < 0 || j >= lines.length) return; const next = [...lines]; [next[i], next[j]] = [next[j], next[i]]; compute(next); },
  };
  // A default naming the key ("{key}/{line}", a batch number) cannot be
  // filled before the work item exists: it shows as "{key}/1" and says so.
  const keyNote = (def.columns || []).some((c) => /\{key\}/i.test(String(c.default || '')))
    ? <div className="li-muted li-pad">{'{key}'} is filled with the new work item's key when it is created.</div> : null;
  const table = <><LineItemsTable definition={def} lines={lines} totals={totals} handlers={handlers} busy={busy} fields={fields} projectKey={projectKey} fieldId={fieldId} />{keyNote}</>;
  const doc = def.document;
  if (!doc || !paper) return <>{error && <div className="li-error">{error}</div>}{table}</>;
  const company = doc.company || { name: '', lines: [], website: '' };
  const logo = doc.logo || lookAndFeel?.logoDataUri || null;
  // Only a valueOf the caller GAVE counts: every plain object inherits
  // Object.prototype.valueOf, which returns the paper itself — a paper with
  // no party yet would draw that object and crash the page (React #31).
  const read = Object.prototype.hasOwnProperty.call(paper, 'valueOf') && typeof paper.valueOf === 'function' ? paper.valueOf : null;
  const valueOf = (name) => { const v = read && name ? read(name) : ''; return v == null || typeof v === 'object' ? '' : String(v); };
  const dateIn = paper.date || valueOf(doc.dateField) || new Date().toISOString().slice(0, 10);
  const partyIn = paper.party || (doc.party ? valueOf(doc.party.field) : '');
  const day = dateIn ? new Date(`${String(dateIn).slice(0, 10)}T00:00:00`) : null;
  const dateText = day && !Number.isNaN(day.getTime()) ? day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  return (
    <div className="li-paper">
      <div className="li-paper-head">
        <div className={`li-paper-co${company.name ? '' : ' li-paper-co-stack'}`}>
          {logo && <img src={logo} alt="" className="li-paper-logo" />}
          <div>
            {company.name && <div className="li-paper-coname">{company.name}</div>}
            {[...company.lines, company.website].filter(Boolean).map((l) => <div key={l}>{l}</div>)}
          </div>
        </div>
        <div className="li-paper-title">
          <div className="li-paper-h">{doc.title}</div>
          <div className="li-paper-kv"><strong>{doc.numberLabel}</strong><span className="li-muted">{paper.number || 'on create'}</span></div>
          <div className="li-paper-kv"><strong>{doc.dateLabel}</strong><span>{dateText}</span></div>
          {(doc.fields || []).map((x) => { const v = valueOf(x.field, ''); return v ? <div key={x.field} className="li-paper-kv"><strong>{x.label}</strong><span>{/^\d{4}-\d{2}-\d{2}/.test(v) ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : v}</span></div> : null; })}
        </div>
      </div>
      {doc.party && (
        <div className="li-paper-parties">
          <div><div className="li-paper-label">{doc.party.label}</div><div>{partyIn || <span className="li-muted">— choose the {doc.party.field.toLowerCase()} above —</span>}</div></div>
        </div>
      )}
      {error && <div className="li-error">{error}</div>}
      {table}
    </div>
  );
}

// ---- the document, on screen ---------------------------------------------------
// The page the PDF prints, drawn live around the editable table: letterhead
// and title, the number as a barcode (or a QR code of the work item's link,
// document.qr), the parties; under it the toggle (when the document
// converts), the PDF, full screen and — converted — the lock. A document
// with a SIGNED approval (convert.sign.steps) has its Authorisation under
// the lines: each step signed in order by its own people, the converting
// step's signature making it the order; the view switch reads the quote or
// the order (a draft until signed) without changing anything.
const fieldText = (v) => display(v);
const qrCache = new Map();   // link → Promise<base64 png | null>
export function useDocumentQr(doc, key) {
  const link = qrLink(doc, key);
  const [png, setPng] = useState(null);
  useEffect(() => {
    if (!link) { setPng(null); return undefined; }
    let on = true;
    if (!qrCache.has(link)) qrCache.set(link, lineItemsApi.qr(link).catch(() => { qrCache.delete(link); return null; }));
    qrCache.get(link).then((p) => { if (on) setPng(p); });
    return () => { on = false; };
  }, [link]);
  return { link, png };
}

// #rrggbb mixed toward white by `t` (0..1): the document's accent made readable on a dark page.
function lighten(hex, t) {
  const m = /^#([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (sh) => Math.round(((n >> sh) & 255) + (255 - ((n >> sh) & 255)) * t);
  return `#${[16, 8, 0].map((sh) => ch(sh).toString(16).padStart(2, '0')).join('')}`;
}

// A header field changed from the paper (document.fields[].edit / party.edit): the work
// item's own field, by its editmeta — a list (an option), a person (a user) or text. The
// value shows as it does on the paper; ✎ (or a click) opens the editor in its place.
function PaperFieldEdit({ meta, value, text, issueKey, onSave, block = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const searchUsers = useUserSearch();
  const type = meta.schema?.type;
  const done = async (v) => { setEditing(false); await onSave(meta.id, v); };
  const Tag = block ? 'div' : 'span';
  if (!editing) {
    return (
      <Tag className="li-paper-edit" role="button" tabIndex={0} title={`Change ${meta.name || 'this'} on the work item`}
        onClick={() => { setDraft(typeof value === 'string' || typeof value === 'number' ? String(value) : (text || '')); setEditing(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { setDraft(text || ''); setEditing(true); } }}>
        {text || <span className="li-muted">—</span>}<span className="li-paper-edit-icon" aria-hidden="true">✎</span>
      </Tag>
    );
  }
  if (Array.isArray(meta.allowedValues) && meta.allowedValues.length) {
    return (
      <Tag className="li-paper-editor">
        <SearchableSelect options={meta.allowedValues} value={value?.id || ''} defaultOpen
          onChange={(id) => { const o = meta.allowedValues.find((x) => String(x.id) === String(id)); done(o ? { id: String(o.id) } : null); }}
          onDismiss={() => setEditing(false)} />
      </Tag>
    );
  }
  if (type === 'user') {
    return (
      <Tag className="li-paper-editor">
        <UserEditor value={value || null} onChange={() => {}}
          onSearchUsers={(q) => searchUsers(q, { fieldId: meta.id, issueKey })}
          onAutoSave={(u) => done(u ? { accountId: u.accountId } : null)} />
        <button type="button" className="li-btn li-btn-x" onClick={() => setEditing(false)} title="Cancel">✕</button>
      </Tag>
    );
  }
  return (
    <Tag className="li-paper-editor">
      <input className="li-input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') done(type === 'number' ? (draft.trim() === '' ? null : Number(draft)) : (draft.trim() || null)); if (e.key === 'Escape') setEditing(false); }}
        onBlur={() => { if (draft !== (text || '')) done(type === 'number' ? (draft.trim() === '' ? null : Number(draft)) : (draft.trim() || null)); else setEditing(false); }} />
    </Tag>
  );
}

function DocumentView({ state, issueKey, fieldId, fields, onChanged, onWorkItemStatus, children, downloads = null }) {
  const def = state.definition; const st = state.document || {};
  const lookAndFeel = useLookAndFeel();
  const [issue, setIssue] = useState(null);
  const [asking, setAsking] = useState(null);   // the convert prompt: { value } | null
  const [view, setView] = useState(null);       // signed approval: 'first' | 'converted' to read; null = the stage
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const conv = st.convert;
  const converted = st.stage === 'converted';
  const sign = conv?.sign || null;
  const steps = sign?.steps || [];
  const signatures = sign ? st.signatures || {} : {};
  // Signed approval: through approval it reads as the order, and the switch
  // reads its quote too (changing nothing); before approval it is the quote.
  const shown = sign && converted ? (view || 'converted') : (converted ? 'converted' : 'first');
  const firstHead = st.first ? { ...def.document, ...st.first } : def.document;
  const convHead = conv ? { ...def.document, title: conv.title, numberLabel: conv.numberLabel || def.document.numberLabel, numberField: conv.numberField || def.document.numberField, fileStem: conv.fileStem || def.document.fileStem } : def.document;
  const doc = !sign ? def.document : shown === 'first' ? firstHead : convHead;
  const draftOrder = false;
  const byName = useCallback((name) => (fields && name ? fields.get(norm(name)) : null), [fields]);
  const convertNumber = conv?.numberField ? byName(conv.numberField) : null;
  useEffect(() => {
    if (!fields) return undefined;
    let on = true;
    const want = ['summary', 'created'];
    for (const name of [def.document.numberField, def.document.dateField, def.document.party?.field, ...(def.document.party?.lines || []), conv?.numberField, st.first?.numberField, ...(def.document.fields || []).map((x) => x.field)]) { const f = byName(name); if (f) want.push(f.id); }
    api.getIssue(issueKey, want.join(',')).then((d) => { if (on) setIssue(d); }).catch(() => { if (on) setIssue({ key: issueKey, fields: {} }); });
    return () => { on = false; };
  }, [fields, issueKey, def.document, st.stage, byName, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  // The header follows the work item: a Due Date (or number, or party)
  // changed beside the table redraws it — every save is broadcast.
  useEffect(() => {
    const onChanged = (p) => { if (p?.event === 'jira:issue_updated' && p.issueKey === issueKey) setTick((t) => t + 1); };
    socket.on('issues-changed', onChanged);
    return () => { socket.off('issues-changed', onChanged); };
  }, [issueKey]);
  // Header fields marked edit: true change the work item's own field from the paper (the
  // same write as its screen). Editmeta says which may be changed here, and how.
  const editable = [...(def.document.fields || []).filter((x) => x.edit).map((x) => x.field), ...(def.document.party?.edit ? [def.document.party.field] : [])];
  const [editMeta, setEditMeta] = useState(null);
  useEffect(() => {
    if (!editable.length || !state.canEdit) { setEditMeta(null); return undefined; }
    let on = true;
    api.getEditMeta(issueKey).then((m) => { if (on) setEditMeta(m?.fields || {}); }).catch(() => { if (on) setEditMeta({}); });
    return () => { on = false; };
  }, [issueKey, state.canEdit, editable.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const metaOf = (name) => { const fld = byName(name); return fld && editMeta && editMeta[fld.id] ? { id: fld.id, ...editMeta[fld.id] } : null; };
  const saveField = async (fieldId, value) => {
    setErr(null);
    try { await api.updateIssue(issueKey, { [fieldId]: value }); setTick((t) => t + 1); } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { setView(null); }, [converted]);
  // Full screen: Esc closes it; the page underneath does not scroll.
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [full]);
  const f = issue?.fields || {};
  const valueOf = (name, fallback) => { const fld = byName(name); return (fld && fieldText(f[fld.id])) || fallback; };
  const number = doc.numberField ? valueOf(doc.numberField, issueKey) : issueKey;
  const date = doc.dateField ? valueOf(doc.dateField, f.created) : f.created;
  const day = date ? new Date(date) : null;
  const dateText = day && !Number.isNaN(day.getTime()) ? day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const party = doc.party ? valueOf(doc.party.field, '') : '';
  const company = doc.company || { name: '', lines: [], website: '' };
  // No shipTo = no ship-to block (a stock pull); shipTo with no lines = us.
  const shipLines = !doc.shipTo ? [] : doc.shipTo.lines.length ? doc.shipTo.lines : [company.name, ...company.lines].filter(Boolean);
  const logo = doc.logo || lookAndFeel?.logoDataUri || null;
  const qr = useDocumentQr(doc, issueKey);
  const bc = !doc.qr && doc.barcode && number ? code128(number) : null;

  const go = async (to, poNumber, sig) => {
    setBusy(true); setErr(null);
    try {
      if (to === 'converted' && convertNumber && poNumber != null && poNumber.trim() !== fieldText(f[convertNumber.id])) {
        await api.updateIssue(issueKey, { [convertNumber.id]: poNumber.trim() });
      }
      await lineItemsApi.convert(issueKey, fieldId, to, sig);
      setAsking(null); setSigning(false); onChanged();
    } catch (e) { setErr(errText(e)); if (sig) { setBusy(false); throw e; } }
    setBusy(false);
  };
  const startConvert = () => {
    if (convertNumber) setAsking({ value: fieldText(f[convertNumber.id]) });
    else go('converted');
  };
  const mayToggle = !!conv && !sign && conv.canConvert && state.canEdit;
  const toggleTitle = !conv || sign ? '' : !state.canEdit ? 'You may not edit this work item.'
    : !conv.canConvert ? `Offered while the work item is in its approval step (now: ${st.status || '—'}).` : '';
  // Signed approval: signed in its box (the order's number is the order's
  // own field — never asked with a signature).
  // A step's `then` moved the work item on (moved: the new status — the screen shows it at once),
  // or a hold kept it where it is (held: why; the signature stands).
  const signStep = async (step, sig, extra) => {
    const r = await lineItemsApi.sign(issueKey, fieldId, step.id, sig, extra);
    if (r && r.moved && onWorkItemStatus) onWorkItemStatus(r.moved);
    setErr(r && r.held && r.held.length ? `Signed — but it stays in ${st.status || 'this status'}: ${r.held.join(' ')}` : null);
    onChanged();
  };
  const clearStep = async (step) => {
    const s = signatures[step.id];
    if (!window.confirm(`Clear your ${step.label} signature${s ? ` (${s.name})` : ''}?${step.converts ? ` It becomes the ${conv.firstTitle.toLowerCase()} again.` : step.locks ? ' The lines can be changed again.' : ''}`)) return;
    setBusy(true); setErr(null);
    try { await lineItemsApi.clearSignature(issueKey, fieldId, step.id); onChanged(); } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  const paper = (
    <div className={`li-paper${full ? ' li-paper-full' : ''}`} style={doc.accent ? { '--li-accent-doc': doc.accent, '--li-accent-doc-dark': lighten(doc.accent, 0.45) } : undefined}>
      <div className="li-paper-head">
        <div className={`li-paper-co${company.name ? '' : ' li-paper-co-stack'}`}>
          {logo && <img src={logo} alt="" className="li-paper-logo" />}
          <div>
            {company.name && <div className="li-paper-coname">{company.name}</div>}
            {[...company.lines, company.website].filter(Boolean).map((l) => <div key={l}>{l}</div>)}
          </div>
        </div>
        <div className="li-paper-title">
          <div className="li-paper-h">{doc.title}</div>
          {qr.link && (qr.png
            ? <a href={qr.link} target="_blank" rel="noopener noreferrer" title={`Scan to open ${issueKey}: ${qr.link}`}><img className="li-paper-qr" src={`data:image/png;base64,${qr.png}`} alt={`QR code — ${issueKey}`} /></a>
            : <div className="li-paper-qr li-paper-qr-wait" aria-hidden="true" />)}
          {bc && (
            <svg className="li-paper-bc" viewBox={`0 0 ${bc.width} 30`} preserveAspectRatio="none" aria-label={`Barcode ${number}`}>
              {bc.bars.map((b, i) => <rect key={i} x={b.x} y={0} width={b.w} height={30} />)}
            </svg>
          )}
          <div className="li-paper-kv"><strong>{doc.numberLabel}</strong><span>{number}</span></div>
          <div className="li-paper-kv"><strong>{doc.dateLabel}</strong><span>{dateText}</span></div>
          {(doc.fields || []).map((x) => {
            const v = valueOf(x.field, '');
            const shownValue = /^\d{4}-\d{2}-\d{2}/.test(v) ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : v;
            const meta = x.edit ? metaOf(x.field) : null;
            if (meta) return <div key={x.field} className="li-paper-kv"><strong>{x.label}</strong><PaperFieldEdit meta={meta} value={f[meta.id]} text={shownValue} issueKey={issueKey} onSave={saveField} /></div>;
            return v ? <div key={x.field} className="li-paper-kv"><strong>{x.label}</strong><span>{shownValue}</span></div> : null;
          })}
        </div>
      </div>
      {(doc.party || shipLines.length > 0) && (
        <div className="li-paper-parties">
          {doc.party && <div><div className="li-paper-label">{doc.party.label}</div>{doc.party.edit && metaOf(doc.party.field)
              ? <PaperFieldEdit meta={metaOf(doc.party.field)} value={f[metaOf(doc.party.field).id]} text={party} issueKey={issueKey} onSave={saveField} block />
              : <div>{party || <span className="li-muted">— no {doc.party.field.toLowerCase()} yet —</span>}</div>}
            {(doc.party.lines || []).map((name) => { const v = valueOf(name, ''); return v ? <div key={name}>{v}</div> : null; })}</div>}
          {shipLines.length > 0 && <div><div className="li-paper-label">{doc.shipTo?.label || 'Ship To Address'}</div>{shipLines.map((l) => <div key={l}>{l}</div>)}</div>}
        </div>
      )}

      {st.locked && (
        <div className="li-lock" role="status">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          <span>{sign
            ? `Signed — no line can be added, changed or removed.${converted ? '' : ' Clear the signature (the signer only), or move the work item back before approval, to change them.'}`
            : `This ${def.document.title.toLowerCase()} is final — no line can be added, changed or removed. Move the work item back before approval to change it.`}</span>
        </div>
      )}


      {children}

      {doc.notes && <div className="li-paper-notes"><div className="li-paper-label">Notes</div><div>{doc.notes}</div></div>}

      {sign && <Authorisation steps={steps} signatures={signatures} passed={st.passed || {}} me={st.me} busy={busy} onSign={signStep} onClear={clearStep} convertTitle={doc.convert?.title || ''} location={sign.location !== false} />}

      <div className="li-paper-foot">
        {conv && !sign && (
          <div className="li-switch" title={toggleTitle} role="group" aria-label="Document">
            <button type="button" className={!converted ? 'li-on' : ''} disabled={busy || !mayToggle || !converted} onClick={() => go('first')}>{conv.firstTitle}</button>
            <button type="button" className={converted ? 'li-on' : ''} disabled={busy || !mayToggle || converted} onClick={startConvert}>{conv.title}</button>
          </div>
        )}
        {conv && sign && converted && (
          <SlideSwitch options={[{ id: 'first', label: conv.firstTitle }, { id: 'converted', label: conv.title }]} value={shown} onChange={setView}
            title={`Through approval: read the ${conv.title.toLowerCase()} or the ${conv.firstTitle.toLowerCase()} it was approved from`} />
        )}
        {conv && !sign && mayToggle && !converted && !asking && <span className="li-muted">Approved? Make it the {conv.title.toLowerCase()} — its lines are then final.</span>}
        <span style={{ flex: 1 }} />
        {downloads}
        <button type="button" className="li-btn" onClick={() => setFull((v) => !v)} title={full ? 'Close full screen (Esc)' : `Open this ${doc.title.toLowerCase()} full screen`}>{full ? 'Exit full screen' : '⛶ Full screen'}</button>
        {doc.pdf !== false && <DocumentButton definition={{ ...def, document: doc }} lines={state.lines} totals={state.totals} issueKey={issueKey} fields={fields} qrPng={qr.png} steps={steps} signatures={signatures} draft={draftOrder} />}
      </div>
      {asking && (
        <div className="li-ask">
          <label>{convertNumber?.name || 'Number'}
            <input className="li-input" autoFocus value={asking.value} placeholder={`The ${conv.title.toLowerCase()}'s number`}
              onChange={(e) => setAsking({ value: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter' && asking.value.trim()) go('converted', asking.value); if (e.key === 'Escape') setAsking(null); }} />
          </label>
          <button type="button" className="li-btn li-btn-primary" disabled={busy || !asking.value.trim()} onClick={() => go('converted', asking.value)}>{busy ? 'Converting…' : conv.button}</button>
          <button type="button" className="li-btn" disabled={busy} onClick={() => setAsking(null)}>Cancel</button>
          <span className="li-muted">The lines are locked once it is the {conv.title.toLowerCase()}.</span>
        </div>
      )}
      {err && <div className="li-error">{err}</div>}
    </div>
  );
  if (!full) return paper;
  // Full screen: the same document (the table still works) over the page.
  return createPortal(
    <div className="li-full" role="dialog" aria-modal="true" aria-label={doc.title} onMouseDown={(e) => { if (e.target === e.currentTarget) setFull(false); }}>
      <style>{FULL_LIFT_CSS}</style>
      <button type="button" className="li-full-x" onClick={() => setFull(false)} title="Close full screen (Esc)" aria-label="Close full screen">×</button>
      {paper}
    </div>, document.body);
}

// A slide switch: one rounded track, a white pill that slides BEHIND the
// chosen name (sized to it); the track wears a tint of the accent on any
// stop but the first. ← / → step along. options = [{ id, label }].
function SlideSwitch({ options, value, onChange, title }) {
  const i = Math.max(0, options.findIndex((o) => o.id === value));
  const refs = useRef([]);
  const trackRef = useRef(null);
  const [pill, setPill] = useState({ x: 0, w: 0 });
  useLayoutEffect(() => {
    const measure = () => { const el = refs.current[i]; if (el) setPill({ x: el.offsetLeft, w: el.offsetWidth }); };
    measure();
    const t = trackRef.current;
    const ro = t && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(t);
    return () => { if (ro) ro.disconnect(); };
  }, [i, options]);
  const step = (d) => onChange(options[Math.min(options.length - 1, Math.max(0, i + d))].id);
  return (
    <div ref={trackRef} className={`li-slide${i > 0 ? ' li-slide-on' : ''}`} role="radiogroup" aria-label={title} title={title} tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'ArrowRight') { e.preventDefault(); step(1); } else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); } }}>
      <span className="li-slide-thumb" aria-hidden="true" style={{ width: pill.w, transform: `translateX(${pill.x}px)` }} />
      {options.map((o, k) => (
        <button key={o.id} ref={(el) => { refs.current[k] = el; }} type="button" role="radio" aria-checked={k === i}
          className={`li-slide-opt${k === i ? ' li-slide-cur' : ''}`} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}

// Full screen sits over every page layer (a Warehouse pop-up is at 4000);
// the product's body-level layers opened from it are lifted over it.
const FULL_LIFT_CSS = '.modal-overlay{z-index:4700!important}.ss-dropdown,.typeahead-dropdown{z-index:4800!important}.att-preview-overlay{z-index:4900!important}';

// The document the table exports as (definition.document): one button above
// the table, named after the document; the PDF is built in the browser from
// the lines and totals the table shows and the work item's own fields — with
// its QR code and the approver's signature when it has them.
function DocumentButton({ definition: def, lines, totals, issueKey, fields, qrPng = null, steps = [], signatures = {}, draft = false }) {
  const [state, setState] = useState('');
  const lookAndFeel = useLookAndFeel();
  const go = async () => {
    setState('busy');
    try {
      const { buildDocumentPdf } = await import('./document-pdf');
      const want = ['summary', 'created'];
      const byName = (name) => (fields && name ? fields.get(norm(name)) : null);
      for (const name of [def.document.numberField, def.document.dateField, def.document.party?.field, ...(def.document.party?.lines || []), ...(def.document.fields || []).map((x) => x.field)]) { const f = byName(name); if (f) want.push(f.id); }
      const issue = await api.getIssue(issueKey, want.join(','));
      const png = qrPng || (def.document.qr ? await lineItemsApi.qr(qrLink(def.document, issueKey)).catch(() => null) : null);
      await buildDocumentPdf({ definition: def, lines, totals, issue, fieldByName: byName, logoDataUri: lookAndFeel?.logoDataUri || null, qrPng: png, steps, signatures, draft });
      setState('');
    } catch (e) { setState(errText(e)); setTimeout(() => setState(''), 5000); }
  };
  const err = state && state !== 'busy';
  return (
    <div className="li-toolbar li-toolbar-inline">
      <button type="button" className="li-btn" onClick={go} disabled={state === 'busy' || !lines.length} title={err ? state : (lines.length ? `Download this ${def.document.title.toLowerCase()} as a PDF` : 'Add a line first')}>
        {state === 'busy' ? 'Building…' : err ? 'Could not build the PDF' : `${def.document.title} (PDF)`}
      </button>
    </div>
  );
}

// The table itself. handlers = null → read-only (a viewer, or the preview).
export function LineItemsTable({ definition: def, lines, totals, handlers, busy = false, options = {}, fields = null, projectKey = null, issueKey = null, fieldId = null }) {
  const canEdit = !!handlers && !handlers.bookOnly;
  const bookOnly = !!(handlers && handlers.bookOnly);
  const shownTotals = (totals || []).filter((t) => t.value != null || lines.length);
  return (
    <div className="li-doc">
      <div className="li-scroll">
        <table className="li-table">
          <thead><tr>
            {def.columns.map((c) => <th key={c.id} className={alignClass(c)} style={c.width ? { width: c.width } : undefined}>{c.label}{c.required && canEdit ? ' *' : ''}</th>)}
            {canEdit && <th className="li-actions-col" aria-label="Line actions" />}
          </tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.id ?? i} className={`li-row${l.pending ? ' li-pending' : ''}`}>
                {def.columns.map((c) => (
                  <td key={c.id} className={alignClass(c)}>
                    <Cell def={def} col={c} line={l} canEdit={(bookOnly && c.book === 'enter' && !busy && !l.pending) || canEdit && !busy && !l.pending && c.type !== 'formula' && !c.readOnly && !c.fromDue} options={options[c.id]} fields={fields} projectKey={projectKey} fieldId={fieldId}
                      onSave={(patch) => handlers.onSave(l, patch)} />
                    {c.type === 'product' && (l.errors || []).map((e) => <div key={e} className="li-line-err">{e}</div>)}
                  </td>
                ))}
                {canEdit && (l.pending ? <td className="li-actions"><span className="li-muted">Saving…</span></td> : (
                  <td className="li-actions">
                    <button type="button" className="li-icon-btn" title="Move up" disabled={busy || i === 0} onClick={() => handlers.onMove(i, -1)}>↑</button>
                    <button type="button" className="li-icon-btn" title="Move down" disabled={busy || i === lines.length - 1} onClick={() => handlers.onMove(i, 1)}>↓</button>
                    {def.lockLines ? null : <button type="button" className="li-icon-btn li-danger" title="Remove this line" disabled={busy} onClick={() => handlers.onRemove(l)}>✕</button>}
                  </td>
                ))}
              </tr>
            ))}
            {canEdit && !def.backlinks && !(def.maxLines && lines.length >= def.maxLines) && <NewLine def={def} options={options} fields={fields} projectKey={projectKey} busy={busy} onAdd={handlers.onAdd} issueKey={issueKey} fieldId={fieldId} lines={lines} />}
          </tbody>
        </table>
        {!lines.length && !canEdit && <div className="li-muted li-pad">{def.backlinks ? `No work item names this one in “${def.backlinks.field}” yet.` : 'No lines yet.'}</div>}
      </div>
      {shownTotals.length > 0 && (
        <div className="li-totals">
          {shownTotals.map((t) => (
            <div key={t.id} className={`li-total${t.strong ? ' li-total-strong' : ''}`}>
              <span>{t.label}{t.editable && <RateBox total={t} onRate={canEdit && handlers.onRate && !busy ? (r) => handlers.onRate(t, r) : null} />}</span><span>{t.value == null ? '—' : fmtMoney(t.value, def.currency)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// An editable percent total's rate beside its label: "VAT [15] %". Saved on
// Enter or leaving the box; emptied = back to the default. Read-only, the
// rate is shown as words.
function RateBox({ total, onRate }) {
  const shown = total.rate == null ? '' : String(total.rate);
  const [v, setV] = useState(shown);
  useEffect(() => { setV(shown); }, [shown]);
  if (!onRate) return shown ? <span className="li-muted li-rate-text"> {shown.replace('.', ',')} %</span> : null;
  const commit = () => {
    const t = v.trim().replace(',', '.');
    if (t === shown) return;
    if (t !== '' && (!Number.isFinite(Number(t)) || Number(t) < 0 || Number(t) > 100)) { setV(shown); return; }
    onRate(t === '' ? null : Number(t));
  };
  return (
    <span className="li-rate">
      <input className="li-input li-rate-input" type="text" inputMode="decimal" autoComplete="off" value={v} aria-label={`${total.label} rate (%)`}
        title={`The ${total.label} rate on this work item — 0 for a supplier not VAT registered${total.defaultRate != null ? `; empty = ${total.defaultRate} %` : ''}`}
        onChange={(e) => setV(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') { setV(shown); } }} />
      <span className="li-muted">%</span>
      <span className="li-rate-edit" aria-hidden="true" title="Editable — type the rate">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
      </span>
    </span>
  );
}

// The cell's face: style (bold / chip / muted) and alignment from the definition.
function Face({ col, text, sub, free }) {
  if (text === '' || text == null) return <span className="li-muted">—</span>;
  const inner = col.style === 'chip' ? <span className="li-chip">{text}</span> : <span className={col.style === 'bold' ? 'li-bold' : col.style === 'muted' ? 'li-muted' : undefined}>{text}</span>;
  return (
    <>
      {inner}
      {free && <span className="li-free" title="Not in the catalogue">free text</span>}
      {sub && <div className="li-sub">{sub}</div>}
    </>
  );
}

// A work item named in a cell, as a link: it opens in a Stride tab (a plain link outside one).
function WorkItemLink({ itemKey, children }) {
  const tab = useOptionalTab();
  const href = `/browse/${encodeURIComponent(itemKey)}`;
  const open = (e) => {
    if (!tab || typeof tab.openTab !== 'function' || e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    tab.openTab(href, itemKey);
  };
  return <a className="li-link" href={href} onClick={open} title={itemKey}>{children}</a>;
}

// One saved cell: shows the value; a click edits it; leaving saves it.
function Cell({ def, col, line, canEdit, options, fields, projectKey, fieldId, onSave }) {
  const [editing, setEditing] = useState(false);
  if (col.type === 'product') {
    if (editing) {
      // A new product fills the line's EMPTY fillFromLast cells (its last price).
      return <ProductPicker col={col} fields={fields} def={def} fieldId={fieldId} projectKey={projectKey} autoFocus initial={line.productLabel}
        onPick={async (p) => {
          setEditing(false);
          const fill = await fillFromLast(def, fieldId, p);
          const extra = Object.fromEntries(Object.entries(fill).filter(([k]) => line.vals?.[k] == null || line.vals[k] === ''));
          onSave({ productKey: p.key || null, productLabel: p.label, ...(p.created ? { createdProduct: true } : {}), ...(Object.keys(extra).length ? { vals: extra } : {}) });
        }} onCancel={() => setEditing(false)} />;
    }
    // A named catalogue's item shows its show fields under the label (they come with the line's info).
    const sub = col.catalog ? Object.values(line.info?.item?.show || {}).map(display).filter(Boolean).join(' · ') : '';
    const face = <Face col={col} text={line.productLabel || line.productKey || ''} sub={sub} free={!line.productKey && !!line.productLabel} />;
    if (canEdit) return <button type="button" className="li-cell-btn" onClick={() => setEditing(true)} title={line.productKey ? `${line.productKey} — click to change` : 'Click to change'}>{face}</button>;
    // Read-only, a picked work item is a link to it (a read-back table lists the work items naming this one);
    // a catalogue's item is not a work item — it has nothing to open.
    return line.productKey && !col.catalog ? <WorkItemLink itemKey={line.productKey}>{face}</WorkItemLink> : <span>{face}</span>;
  }
  const text = cellText(def, col, line);
  // A capped quantity says what the product holds: "of 12 on hand".
  const cap = col.max ? line.info?.max?.[col.id] : undefined;
  const capSub = cap != null ? `of ${fmtNumber(cap)} ${capWord(col)}` : null;
  if (!editing) {
    const face = <Face col={col} text={text} sub={capSub} />;
    return canEdit ? <button type="button" className={`li-cell-btn ${alignClass(col)}`} onClick={() => setEditing(true)}>{face}</button> : face;
  }
  const value = line.vals?.[col.id];
  const done = (v) => { setEditing(false); if (String(v ?? '') !== String(value ?? '')) onSave({ vals: { [col.id]: v } }); };
  return <ValueInput col={col} value={value} options={options} autoFocus onCommit={done} onCancel={() => setEditing(false)} />;
}

function ValueInput({ col, value, options, autoFocus, onCommit, onCancel, onChange, inputRef, onEnter, max }) {
  const numeric = col.type === 'number' || col.type === 'money';
  const common = {
    className: `li-input${numeric ? ' li-a-right' : ''}`, autoFocus, ref: inputRef,
    // A line's cells are not a person's details: Chrome read a batch number
    // ("TOOLS-481/2") as a licence plate and offered to save it to Wallet.
    autoComplete: 'off',
    onKeyDown: (e) => {
      if (e.key === 'Escape' && onCancel) onCancel();
      if (e.key === 'Enter') { e.preventDefault(); if (onEnter) onEnter(); else e.currentTarget.blur(); }
    },
  };
  if (col.type === 'option') {
    return (
      <select {...common} value={value ?? ''} onChange={(e) => (onChange ? onChange(e.target.value) : onCommit(e.target.value))} onBlur={onChange ? undefined : (e) => onCommit(e.target.value)}>
        <option value="">—</option>
        {(options || []).map((o) => <option key={o.id} value={o.value}>{o.value}</option>)}
        {value && !(options || []).some((o) => o.value === value) && <option value={value}>{value}</option>}
      </select>
    );
  }
  const type = numeric ? 'number' : col.type === 'date' ? 'date' : 'text';
  if (onChange) return <input {...common} type={type} step="any" min={col.min} max={max} inputMode={numeric ? 'decimal' : undefined} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  return <input {...common} type={type} step="any" min={col.min} inputMode={numeric ? 'decimal' : undefined} defaultValue={value ?? ''} onBlur={(e) => onCommit(e.target.value)} />;
}

// The add row: product first, then the rest. The line saves ITSELF as soon
// as the product is picked and every required column has a value — the
// definition's defaults ("{key}" = this work item's key, "{today}") count,
// so a purchase order line is one pick. A required column without a
// default is typed first; Enter saves too. Formula cells fill in once the
// line is saved.
// A line typed into the row saves itself when the row is left — a click
// elsewhere, Tab past the last box, the work item closed — so nothing typed
// is lost for want of Enter (an invoice's lines have no product to pick and
// were saved by Enter alone; leaving the row dropped them). A refresh of the
// table keeps what is being typed: only the boxes not yet touched take the
// new defaults.
// No key yet (a create form): "{key}" stays as it is — the server fills it
// with the new work item's key when the line is added to it.
const fillTokens = (v, issueKey, lineNo = 1) => (typeof v === 'string'
  ? v.replace(/\{key\}/gi, issueKey || '{key}').replace(/\{today\}/gi, new Date().toISOString().slice(0, 10)).replace(/\{line\}/gi, String(lineNo))
  : v);
const uniqueKey = (v) => String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
// A new line's starting values. A unique column whose default counts lines
// ("{key}/{line}") takes the next number not already used on the work item.
function blankFor(def, issueKey, lines) {
  const out = {};
  for (const c of def.columns) {
    if (c.type === 'product' || c.type === 'formula' || c.fromDue) continue;
    let n = (lines?.length || 0) + 1;
    let v = fillTokens(c.default ?? '', issueKey, n);
    if (c.unique && /\{line\}/i.test(String(c.default || ''))) {
      const used = new Set((lines || []).map((l) => uniqueKey(l.vals?.[c.id])));
      while (used.has(uniqueKey(v)) && n < 10000) { n += 1; v = fillTokens(c.default, issueKey, n); }
    }
    out[c.id] = v;
  }
  return out;
}
// The values a picked product brings to its line: each fillFromLast column's
// value on the product's most recent Done line ({} when there is none).
// Then every fillFrom column's value from the product itself (productInfo).
async function fillFromLast(def, fieldId, p) {
  const cols = (def.columns || []).filter((c) => c.fillFromLast);
  if (!p?.key || !fieldId) return {};
  const out = {};
  await Promise.all(cols.map((c) => lineItemsApi.lastValue(fieldId, p.key, c.id)
    .then((r) => { if (r && r.value != null) out[c.id] = r.value; }).catch(() => {})));
  const info = await productInfoOf(def, fieldId, p.key);
  Object.entries(info?.fill || {}).forEach(([k, v]) => { out[k] = v; });
  return out;
}
// What products bring ({ fill, max } per key), asked once per key for a
// short while (a card list and the line it becomes ask the same).
const infoCache = new Map();
function productInfoOf(def, fieldId, key) {
  if (!key || !fieldId || !(def.columns || []).some((c) => c.fillFrom || c.max)) return Promise.resolve(null);
  return productInfos(fieldId, [key]).then((m) => m[key] || null);
}
async function productInfos(fieldId, keys) {
  const now = Date.now(); const out = {}; const need = [];
  for (const k of keys) { const hit = infoCache.get(`${fieldId}|${k}`); if (hit && now - hit.at < 30000) out[k] = hit.info; else need.push(k); }
  if (need.length) {
    const got = await lineItemsApi.productInfo(fieldId, need).catch(() => ({}));
    for (const k of need) { out[k] = got[k] || null; infoCache.set(`${fieldId}|${k}`, { at: now, info: out[k] }); }
  }
  return out;
}

function NewLine({ def, options, fields, projectKey, busy, onAdd, issueKey, fieldId, lines = [] }) {
  const blank = useMemo(() => blankFor(def, issueKey, lines), [def, issueKey, lines]);
  const [product, setProduct] = useState(null);   // { key, label } | null
  const [info, setInfo] = useState(null);         // what the picked product brings: { fill, max }
  const [vals, setVals] = useState(blank);
  const [pickerKey, setPickerKey] = useState(0);
  const hasProduct = def.columns.some((c) => c.type === 'product');
  const firstValueRef = useRef(null);
  const saving = useRef(false);
  // New defaults (a line saved, the table reloaded) replace only the boxes
  // still holding the old ones — never what is being typed.
  const blankRef = useRef(blank);
  useEffect(() => {
    const prev = blankRef.current; blankRef.current = blank;
    if (prev === blank) return;
    setVals((v) => {
      const next = { ...blank };
      for (const [k, x] of Object.entries(v || {})) if (String(x ?? '') !== String(prev?.[k] ?? '')) next[k] = x;
      return next;
    });
  }, [blank]);
  // Something typed (or a product picked) that is not yet a line.
  const dirty = (p, v) => !!p || def.columns.some((c) => c.type !== 'product' && c.type !== 'formula' && !c.fromDue && !c.readOnly
    && String(v?.[c.id] ?? '').trim() !== '' && String(v?.[c.id] ?? '') !== String(blankRef.current?.[c.id] ?? ''));
  // A unique column (a batch number) may not repeat a value another line holds.
  const dupOf = (v) => def.columns.filter((c) => c.unique && uniqueKey(v[c.id]) && lines.some((l) => uniqueKey(l.vals?.[c.id]) === uniqueKey(v[c.id])));
  // A capped column (a batch's on hand) may not take more than is left of
  // the product after the work item's other lines of it.
  const leftOf = (c, p, inf) => {
    const cap = inf?.max?.[c.id];
    if (cap == null || !p?.key) return null;
    const taken = lines.filter((l) => l.productKey === p.key).reduce((a, l) => a + (Number(l.vals?.[c.id]) || 0), 0);
    return Math.max(0, Math.round((cap - taken) * 1e6) / 1e6);
  };
  const overCap = (p, v, inf) => def.columns.some((c) => c.max && leftOf(c, p, inf) != null && Number(v[c.id]) > leftOf(c, p, inf));
  const complete = (p, v, inf = info) => (!hasProduct || !!p) && !dupOf(v).length && !overCap(p, v, inf)
    && def.columns.every((c) => c.type === 'product' || c.type === 'formula' || c.readOnly || !c.required || String(v[c.id] ?? '').trim() !== '');
  const dups = dupOf(vals);
  const add = (p = product, v = vals) => {
    if (saving.current || busy || !complete(p, v)) return;
    saving.current = true;
    // created: the picker has just made this product (archived again if the line lets it go).
    Promise.resolve(onAdd({ productKey: p?.key || null, productLabel: p?.label || '', vals: v, ...(p?.created ? { createdProduct: true } : {}) })).finally(() => { saving.current = false; });
    setProduct(null); setInfo(null); setVals(blankRef.current); setPickerKey((k) => k + 1);
  };
  // Leaving the row saves what was typed; so does the work item closing.
  const latest = useRef({ product, vals }); latest.current = { product, vals };
  const addRef = useRef(add); addRef.current = add;
  const leave = () => { const { product: p, vals: v } = latest.current; if (dirty(p, v)) addRef.current(p, v); };
  const leaveRef = useRef(leave); leaveRef.current = leave;
  useEffect(() => () => leaveRef.current(), []);
  // Left while the line before was still saving: save it once that is done.
  const rowRef = useRef(null);
  useEffect(() => {
    if (busy || !rowRef.current || rowRef.current.contains(document.activeElement)) return;
    leaveRef.current();
  }, [busy]);
  const onRowBlur = (e) => {
    const row = e.currentTarget;
    if (row.contains(e.relatedTarget)) return; // to another box of the same line
    // A product picker's list opens outside the row: wait for focus to land.
    setTimeout(() => { if (!row.isConnected || !row.contains(document.activeElement)) leaveRef.current(); }, 0);
  };
  let firstValue = true;
  return (
    <tr className="li-new" ref={rowRef} onBlur={onRowBlur}>
      {def.columns.map((c) => {
        if (c.type === 'product') {
          return (
            <td key={c.id}>
              {product ? (
                <button type="button" className="li-cell-btn" onClick={() => { setProduct(null); setInfo(null); setPickerKey((k) => k + 1); }} title="Pick another">
                  <span className="li-bold">{product.label}</span>
                  {!product.key && <span className="li-free">free text</span>}
                </button>
              ) : (
                <ProductPicker key={pickerKey} col={c} fields={fields} def={def} fieldId={fieldId} projectKey={projectKey} autoFocus={false} placeholder={`+ Add a line — type a ${c.label.toLowerCase()}`}
                  onPick={async (p) => {
                    // The last price paid fills in BEFORE the line can save itself.
                    const fill = await fillFromLast(def, fieldId, p);
                    const inf = await productInfoOf(def, fieldId, p.key);
                    const v = { ...vals };
                    Object.entries(fill).forEach(([k, x]) => { if (String(v[k] ?? '') === '' || def.columns.find((col) => col.id === k)?.readOnly) v[k] = x; });
                    setVals(v); setInfo(inf);
                    if (complete(p, v, inf)) add(p, v); else { setProduct(p); setTimeout(() => firstValueRef.current?.focus(), 0); }
                  }} />
              )}
            </td>
          );
        }
        if (c.type === 'formula' || c.fromDue) return <td key={c.id} className={alignClass(c)}><span className="li-muted">—</span></td>;
        // Read-only: what the product brings (the standard price), never typed.
        if (c.readOnly) return <td key={c.id} className={alignClass(c)}>{vals[c.id] != null && vals[c.id] !== '' ? <span className="li-muted">{cellText(def, c, { vals })}</span> : <span className="li-muted">{product ? 'no price' : '—'}</span>}</td>;
        const ref = firstValue ? firstValueRef : undefined; firstValue = false;
        const dup = dups.includes(c);
        const left = c.max ? leftOf(c, product, info) : null;
        const over = left != null && Number(vals[c.id]) > left;
        return (
          <td key={c.id} className={`${alignClass(c)}${dup ? ' li-dup' : ''}`} title={dup ? `${c.label} "${vals[c.id]}" is already on another line — each line needs its own.` : undefined}>
            {dup && <div className="li-dup-note">Already used</div>}
            <ValueInput col={c} value={vals[c.id]} options={options[c.id]} inputRef={ref} max={left ?? undefined}
              onChange={(v) => { const next = { ...vals, [c.id]: v }; setVals(next); if (product && c.required && complete(product, next)) add(product, next); }} onEnter={() => add()} />
            {left != null && <div className={over ? 'li-cap li-cap-over' : 'li-cap'}>{over ? `only ${fmtNumber(left)} ${capWord(c)}` : `of ${fmtNumber(left)} ${capWord(c)}`}</div>}
          </td>
        );
      })}
      <td className="li-actions" aria-hidden="true" />
    </tr>
  );
}

// Type to search the catalogue (the column's ZQL) by name, key and the
// shown fields — or, with col.catalog, the named catalogue's own search
// (its items are not work items: show and card name THEIR fields). Offers free text and "create as a new product" when the
// field allows them. With col.card the catalogue reads like a stock list:
// it opens on focus, each product a card (initials, name, category chip,
// on hand — or its kind when it is not stocked), "Create new product"
// always at the foot; creating asks the definition's createProduct.ask
// fields first (a required one must be filled), so a new product is a
// proper product the next order can find again.
const nameKey = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
const initials = (name) => String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '·';
function ProductPicker({ col, fields, def, fieldId = null, onPick, onCancel, autoFocus = true, initial = '', placeholder }) {
  const card = col.card || null;
  const [q, setQ] = useState(initial);
  const [hits, setHits] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);       // the create form: { name, vals: { <field name>: value } } | null
  const [askOpts, setAskOpts] = useState({});   // field name → options
  const [err, setErr] = useState(null);
  const box = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null);
  const catalog = col.catalog || null;
  const showFields = useMemo(() => (catalog ? [] : (col.show || []).map((n) => fields?.get(norm(n))).filter(Boolean)), [col, fields, catalog]);
  const cardFields = useMemo(() => (card && !catalog ? {
    chip: card.chip ? fields?.get(norm(card.chip)) : null,
    qty: card.qty ? fields?.get(norm(card.qty)) : null,
    kind: card.kind ? fields?.get(norm(card.kind)) : null,
  } : {}), [card, fields]);
  const fetchIds = useMemo(() => [...new Set(['summary', ...showFields.map((f) => f.id), ...Object.values(cardFields).filter(Boolean).map((f) => f.id)])].join(','), [showFields, cardFields]);
  // A card list opens on focus; a plain picker once something is typed.
  const menuOpen = open && (!!q.trim() || !!card || !!form);
  // The menu sits against the window under the input (above it when the
  // window has no room below), and follows it on scroll and resize.
  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect(); if (!r) return;
      const vw = window.innerWidth; const vh = window.innerHeight;
      const want = card ? 560 : 520;
      // A phone: the list takes the screen's width, edge to edge less 8px.
      const left = vw <= 640 ? 8 : Math.max(8, Math.min(r.left, vw - Math.min(want, vw - 16) - 8));
      const maxWidth = Math.min(want, vw - left - 8);
      const minWidth = Math.min(Math.max(r.width, card ? 440 : 0), maxWidth);
      setPos(vh - r.bottom < 300 && r.top > vh - r.bottom
        ? { left, bottom: vh - r.top + 4, minWidth, maxWidth }
        : { left, top: r.bottom + 4, minWidth, maxWidth });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [menuOpen, card]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) { setOpen(false); setForm(null); if (onCancel) onCancel(); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onCancel]);

  useEffect(() => {
    const text = q.trim();
    if (!text && !(card && open)) { setHits(null); return undefined; }
    const t = setTimeout(() => {
      if (catalog) {
        lineItemsApi.searchCatalogue(catalog, text, card ? 30 : 15)
          .then((items) => { setHits(items); setActive(0); setErr(null); })
          .catch((e) => { setHits([]); setErr(errText(e)); });
        return;
      }
      const s = zqlStr(text);
      const ors = [`summary ~ "${s}"`, ...showFields.filter((f) => f.schema?.type === 'string').map((f) => `"${zqlStr(f.name)}" ~ "${s}"`)];
      if (/^[A-Za-z][A-Za-z0-9_]*-\d+$/.test(text)) ors.push(`key = "${s.toUpperCase()}"`);
      const zql = text ? `(${col.source}) AND (${ors.join(' OR ')}) ORDER BY summary ASC` : `(${col.source}) ORDER BY summary ASC`;
      api.searchIssues(zql, fetchIds, card ? 30 : 15)
        .then((d) => { setHits(d?.issues || []); setActive(0); setErr(null); })
        .catch((e) => { setHits([]); setErr(errText(e)); });
    }, text ? 220 : 0);
    return () => clearTimeout(t);
  }, [q, col.source, catalog, showFields, fetchIds, card, open]);

  const cp = def.createProduct;
  const ask = useMemo(() => (cp?.ask || []).map((a) => ({ ...a, f: fields?.get(norm(a.field)) || null })), [cp, fields]);
  const openForm = () => {
    setForm({ name: q.trim(), vals: {} }); setErr(null);
    ask.forEach((a) => {
      if (!a.f || askOpts[a.field]) return;
      api.getFieldOptions(a.f.id, cp.projectKey)
        .then((d) => setAskOpts((o) => ({ ...o, [a.field]: (Array.isArray(d) ? d : d?.values || d?.options || []).filter((x) => !x.disabled) })))
        .catch(() => setAskOpts((o) => ({ ...o, [a.field]: [] })));
    });
  };
  const missing = form ? [!form.name.trim() && 'a name', ...ask.filter((a) => a.required && !String(form.vals[a.field] || '').trim()).map((a) => a.field)].filter(Boolean) : [];
  const create = async () => {
    if (!cp) return;
    // Without asked fields the name alone creates (the old one-click); with
    // them, the form must be complete.
    const name = (form ? form.name : q).trim();
    if (!name || (form && (missing.length || sameName))) return;
    setCreating(true); setErr(null);
    try {
      const valueFor = (f, v) => { const t = f.schema?.type; return t === 'option' ? { value: v } : t === 'array' ? [{ value: v }] : t === 'number' ? Number(v) : v; };
      const extra = {};
      Object.entries(cp.fields || {}).forEach(([fname, v]) => { const f = fields?.get(norm(fname)); if (f && v !== '') extra[f.id] = valueFor(f, v); });
      ask.forEach((a) => { const v = String(form?.vals[a.field] || '').trim(); if (a.f && v) extra[a.f.id] = valueFor(a.f, v); });
      const res = await api.createIssue({ fields: { project: { key: cp.projectKey }, issuetype: { name: cp.workType }, summary: name, ...extra } });
      if (!res?.key) throw new Error('The product was not created.');
      setForm(null); setOpen(false);
      onPick({ key: res.key, label: name, created: true });
    } catch (e) { setErr(errText(e)); }
    setCreating(false);
  };
  const startCreate = () => (ask.length ? openForm() : create());

  const stocked = (kind) => !kind || !card?.stocked?.length || card.stocked.some((x) => norm(x) === norm(kind));
  // card.value: what each listed product is worth — on hand × the column's
  // fill (a batch × its Stock Item's standard price), asked for the shown keys.
  const [worth, setWorth] = useState({});
  useEffect(() => {
    if (!card?.value || !fieldId || !hits?.length) return undefined;
    let on = true;
    productInfos(fieldId, hits.map((h) => h.key)).then((m) => { if (on) setWorth(Object.fromEntries(Object.entries(m).map(([k, i]) => [k, i?.fill?.[card.value]]))); });
    return () => { on = false; };
  }, [hits, card, fieldId]);
  // A catalogue's item: { key, label, sub, fields: { <name>: value } } — its fields by name.
  const itemField = (h, name) => {
    if (!name || !h.fields) return null;
    const hit = Object.keys(h.fields).find((n) => norm(n) === norm(name));
    return hit === undefined ? null : h.fields[hit];
  };
  const choices = (hits || []).map((h) => (catalog ? {
    key: h.key, label: h.label || h.key,
    sub: [h.sub || h.key, ...(col.show || []).map((n) => display(itemField(h, n)))].filter(Boolean).join(' · '),
    chip: card?.chip ? display(itemField(h, card.chip)) : '',
    qty: card?.qty ? itemField(h, card.qty) : null,
    kind: card?.kind ? display(itemField(h, card.kind)) : '',
  } : {
    key: h.key, label: h.fields?.summary || h.key,
    sub: [h.key, ...showFields.map((f) => display(h.fields?.[f.id]))].filter(Boolean).join(' · '),
    chip: cardFields.chip ? display(h.fields?.[cardFields.chip.id]) : '',
    qty: cardFields.qty ? h.fields?.[cardFields.qty.id] : null,
    kind: cardFields.kind ? display(h.fields?.[cardFields.kind.id]) : '',
  }));
  const pick = (c) => { setOpen(false); onPick({ key: c.key, label: c.label }); };
  const exact = choices.some((c) => c.label.toLowerCase() === q.trim().toLowerCase());
  const onKey = (e) => {
    if (form) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, Math.max(0, choices.length - 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (choices[active]) pick(choices[active]);
      else if (q.trim() && col.allowFreeText) pick({ key: null, label: q.trim() });
      else if (q.trim() && cp) startCreate();
    } else if (e.key === 'Escape') { setOpen(false); if (onCancel) onCancel(); }
  };

  // NO DUPLICATES: while a new product is being named, the catalogue is
  // searched for it. The same name (case and spacing aside) blocks the
  // create and offers the existing product instead; similar names are
  // shown so the reader can pick one rather than make a second.
  const [similar, setSimilar] = useState(null);   // null = not asked yet
  const formName = form ? form.name.trim() : '';
  useEffect(() => {
    if (!form || !formName) { setSimilar(null); return undefined; }
    const t = setTimeout(() => {
      const words = formName.split(/\s+/).filter((w) => w.length > 1).slice(0, 4);
      const ors = [`summary ~ "${zqlStr(formName)}"`, ...(words.length > 1 ? [words.map((w) => `summary ~ "${zqlStr(w)}"`).join(' AND ')] : [])];
      api.searchIssues(`(${col.source}) AND (${ors.join(' OR ')}) ORDER BY summary ASC`, 'summary', 8)
        .then((d) => setSimilar((d?.issues || []).map((h) => ({ key: h.key, label: h.fields?.summary || h.key }))))
        .catch(() => setSimilar([]));
    }, 250);
    return () => clearTimeout(t);
  }, [formName, col.source, !!form]); // eslint-disable-line react-hooks/exhaustive-deps
  const sameName = (similar || []).find((x) => nameKey(x.label) === nameKey(formName));
  const blocked = missing.length > 0 || !!sameName || (!!formName && similar === null);

  const formView = form && (
    <div className="li-create">
      <div className="li-bold">New product</div>
      <label>Name *<input className="li-input" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
      {sameName && (
        <div className="li-dupbox">
          <strong>“{sameName.label}” is already in the catalogue.</strong>
          <button type="button" className="li-btn li-btn-primary" onClick={() => { setForm(null); pick(sameName); }}>Use it</button>
        </div>
      )}
      {!sameName && similar && similar.length > 0 && (
        <div className="li-similar">
          <div className="li-muted">Similar products already in the catalogue — pick one instead of making a second:</div>
          {similar.slice(0, 5).map((x) => (
            <button key={x.key} type="button" className="li-similar-opt" onClick={() => { setForm(null); pick(x); }}>{x.label}</button>
          ))}
        </div>
      )}
      {ask.map((a) => (
        <label key={a.field}>{a.field}{a.required ? ' *' : ''}
          {a.f && (a.f.schema?.type === 'option' || a.f.schema?.type === 'array') ? (
            <select className="li-input" value={form.vals[a.field] || ''} onChange={(e) => setForm({ ...form, vals: { ...form.vals, [a.field]: e.target.value } })}>
              <option value="">{askOpts[a.field] ? '— pick —' : 'Loading…'}</option>
              {(askOpts[a.field] || []).map((o) => <option key={o.id || o.value} value={o.value}>{o.value}</option>)}
            </select>
          ) : (
            <input className="li-input" value={form.vals[a.field] || ''} onChange={(e) => setForm({ ...form, vals: { ...form.vals, [a.field]: e.target.value } })} />
          )}
        </label>
      ))}
      <div className="li-create-bar">
        <button type="button" className="li-btn li-btn-primary" disabled={creating || blocked} title={sameName ? 'This product already exists — use it' : missing.length ? `Fill in ${missing.join(', ')}` : undefined} onClick={create}>{creating ? 'Creating…' : 'Create and add'}</button>
        <button type="button" className="li-btn" onClick={() => setForm(null)}>Back</button>
        {missing.length > 0 && <span className="li-muted">Needs {missing.join(', ')}</span>}
      </div>
    </div>
  );

  return (
    <div className="li-picker" ref={box}>
      <input ref={inputRef} className="li-input" autoComplete="off" autoFocus={autoFocus} placeholder={placeholder || 'Type to search…'} value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setForm(null); }} onFocus={() => setOpen(true)} onKeyDown={onKey} />
      {menuOpen && pos && (
        <div className={`li-menu${card ? ' li-menu-cards' : ''}`} style={pos}>
          {formView || (
            <>
              <div className={card ? 'li-cards' : undefined}>
                {hits == null && <div className="li-muted li-pad-sm">Searching…</div>}
                {choices.map((c, i) => (card ? (
                  <button key={c.key} type="button" className={`li-card${i === active ? ' li-active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => pick(c)}>
                    <span className="li-card-ico" aria-hidden="true">{initials(c.label)}</span>
                    <span className="li-card-name">{c.label}</span>
                    {c.chip && <span className="li-card-chip">{c.chip}</span>}
                    {card.value && worth[c.key] != null && Number(c.qty) > 0 && (
                      <span className="li-card-value" title={`${fmtNumber(c.qty)} on hand × ${fmtMoney(worth[c.key], def.currency)}`}>{fmtMoney(Number(c.qty) * worth[c.key], def.currency)}</span>
                    )}
                    {stocked(c.kind)
                      ? <span className="li-card-qty" title="On hand">{c.qty == null || c.qty === '' ? '0' : fmtNumber(c.qty)}</span>
                      : <span className="li-card-kind">{c.kind}</span>}
                  </button>
                ) : (
                  <button key={c.key} type="button" className={`li-opt${i === active ? ' li-active' : ''}`} onMouseEnter={() => setActive(i)} onClick={() => pick(c)}>
                    <div className="li-bold">{c.label}</div>
                    <div className="li-sub">{c.sub}</div>
                  </button>
                )))}
                {hits && !choices.length && <div className="li-muted li-pad-sm">{q.trim() ? `No product called “${q.trim()}”.` : 'The catalogue is empty.'}</div>}
              </div>
              {card ? (
                cp && (
                  <button type="button" className="li-card-create" disabled={creating} onClick={startCreate}>
                    <span className="li-card-create-plus" aria-hidden="true">+</span>Create new product{q.trim() && !exact ? <> “<strong>{q.trim()}</strong>”</> : ''}
                  </button>
                )
              ) : hits && !exact && q.trim() && (col.allowFreeText || cp) && (
                <div className="li-opt-extra">
                  {cp && (
                    <button type="button" className="li-opt" disabled={creating} onClick={startCreate}>
                      {creating ? 'Creating…' : <>+ Add “<strong>{q.trim()}</strong>” to the catalogue</>}
                    </button>
                  )}
                  {col.allowFreeText && (
                    <button type="button" className="li-opt" onClick={() => pick({ key: null, label: q.trim() })}>
                      Use “{q.trim()}” as typed
                    </button>
                  )}
                </div>
              )}
              {card && col.allowFreeText && q.trim() && !exact && (
                <button type="button" className="li-opt" onClick={() => pick({ key: null, label: q.trim() })}>Use “{q.trim()}” as typed</button>
              )}
            </>
          )}
          {err && <div className="li-error">{err}</div>}
        </div>
      )}
    </div>
  );
}
